import { PrismicRichText } from '@prismicio/react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import styled, { css } from 'styled-components';

import {
  Title as TitleComponent,
  Description as DescriptionComponent,
  Section as SectionComponent,
  BaseLinkStyle,
} from './ContentSlice';
import { normaIcons, tableIconNames } from './benefit-icons';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { fromTabletBreakpoint, tabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const Description = styled(DescriptionComponent)`
  ${BaseLinkStyle}
  a,
  span {
    color: var(--colors-irisBlue);
  }

  p {
    padding-bottom: 20px;
  }
`;

export const Title = styled(TitleComponent)`
  && {
    ${({ theme }) => theme.typography.scale4}
    font-weight: 700;
  }
`;

export const Table = styled.div`
  margin-top: 100px;

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

export const ColumnTitle = styled.div`
  color: var(--colors-irisBlue);
  ${({ theme }) => theme.typography.titleXS}
  text-transform: uppercase;
  display: flex;
  font-weight: 700;

  & > * {
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-right: 10px;
  color: var(--newBrandColor);

  svg {
    flex: none;
    width: 24px;
    height: 24px;
  }
`;

export const Column = styled.div`
  display: flex;
  box-sizing: border-box;
  ${BaseLinkStyle}

  &:first-child {
    padding-left: 0;
  }

  ${({ $opacity }) => {
    switch ($opacity) {
      case true: {
        return css`
          width: 40%;

          &:first-child {
            width: 20%;
          }

          ${ColumnTitle}, ${IconWrapper} {
            opacity: 0.5;
          }
        `;
      }
      case false:
      default:
        return css`
          width: 30%;

          &:last-child {
            width: 40%;
          }

          ${ColumnTitle}, ${IconWrapper} {
            opacity: 1;
          }
        `;
    }
  }}

  ${fromTabletBreakpoint(css`
    padding: 0 30px;
  `)};
`;

export const IconContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

export const CloseIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: var(--newBrandColor);

  svg {
    width: 42px;
    height: 42px;
    opacity: 0.6;
  }
`;

export const MobileColumnTitle = styled.div`
  display: flex;
  padding-bottom: 10px;
`;

export const ColumnItem = styled(Column)`
  --brandPrimary: #14191e;
  color: rgba(20, 25, 30, 0.75);
  padding: 30px 30px 20px 30px;
  border-bottom: 1px solid var(--colors-irisBlue);
  display: block;
  position: relative;

  &:nth-child(2) {
    border-right: 1px solid var(--colors-irisBlue);
    border-left: 1px solid var(--colors-irisBlue);

    h5 {
      ${({ theme }) => theme.typography.titleLG}
      font-weight: 400;
      color: var(--brandPrimary);
    }

    // em was used in prismic for note under price
    em {
      font-style: normal;
      ${({ theme }) => theme.typography.bodySpecial}
      color: var(--brandPrimary);
    }
  }

  &:first-child {
    padding-left: 0;
  }

  h3 {
    color: var(--brandPrimary);
    ${({ theme }) => theme.typography.titleLG}
    font-weight: 400;
  }

  h4 {
    color: var(--newBrandColor);
    ${({ theme }) => theme.typography.titleXS}
    font-weight: 700;
    margin-bottom: 10px;
  }

  ul {
    text-align: left;
    padding-left: 0;
    list-style: none;
    line-height: 1.7;
    margin-top: 20px;

    li {
      margin-left: 20px;
      position: relative;

      &::before {
        content: '-';
        color: var(--newBrandColor);
        margin-right: 10px;
        position: absolute;
        left: -20px;
      }
    }
  }

  ${tabletBreakpoint(css`
    padding-right: 30px;
  `)};

  ${({ $empty }) => {
    switch ($empty) {
      case true: {
        return css`
          background: #fafafa;
          background-size: cover;

          ${MobileColumnTitle} {
            padding-bottom: 80px;
          }

          ${tabletBreakpoint(css`
            ${CloseIconWrapper} {
              align-items: flex-end;
            }

            ${IconContainer} {
              bottom: 12%;
            }
          `)};
        `;
      }
      case false:
      default:
        return css`
          ${IconContainer} {
            display: none;
          }
        `;
    }
  }}
`;

export const TitleRow = styled.div`
  display: flex;
  border-bottom: 1px solid var(--colors-irisBlue);
`;

export const TableRow = styled.div`
  display: flex;
`;

export const Note = styled.div`
  color: var(--colors-darkBlue);
  ${({ theme }) => theme.typography.bodySpecial}
  margin-top: 50px;
`;

export const MobileTable = styled(Table)`
  text-align: center;
  display: block;

  ${fromTabletBreakpoint(css`
    display: none;
  `)};
`;

export const MobileTableRow = styled(TableRow)`
  flex-direction: column;

  ${Column}, ${ColumnItem} {
    width: 90%;
    margin: 0 auto;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  ${ColumnItem} {
    border: 1px solid var(--colors-irisBlue);
    border-top: none;

    &:first-child {
      border-top: 1px solid var(--colors-irisBlue);
    }
  }

  ${ColumnTitle} {
    color: var(--colors-irisBlue);

    > * {
      color: inherit;
    }
  }
`;

export const Section = styled(SectionComponent)`
  position: relative;

  ${({ $column }) => {
    switch ($column) {
      case true: {
        return css`
          ${Column} {
            &:nth-child(2) {
              width: 80%;
              ${({ theme }) => theme.typography.bodyLead}
              margin: auto;

              ${fromTabletBreakpoint(css`
                border-right: none;
                width: 70%;
              `)};

              ${tabletBreakpoint(css`
                width: 90%;
              `)};
            }
          }
        `;
      }
      case false:
      default:
        return css``;
    }
  }}
`;

export const icons = Object.fromEntries(
  Object.entries(tableIconNames).map(([key, name]) => {
    const Icon = normaIcons[name];
    return [key, <Icon key={key} />];
  })
);

const CloseIcon = normaIcons['times-full'];

export function TableSlice({ slice }) {
  const { primary, context } = transformProperties(slice);
  const { countryName } = context;
  const {
    sectionId,
    column, // true - 2 column table, false - 3 column
    title,
    description,
    countryDescription,
    firstIcon,
    firstColumnTitle,
    secondIcon,
    secondColumnTitle,
    thirdIcon,
    thirdColumnTitle,
    note,
    firstReducedOpacity,
    secondReducedOpacity,
    thirdReducedOpacity,
  } = primary;

  const { fields: table } = slice;

  const defaultTitle = 'Remote Benefits Plans';
  const currentYear = new Date().getFullYear();
  const supportEmail = 'support@remote.com';

  return (
    <Section id={sectionId} $column={column}>
      <ContentContainer>
        <div>
          <Title>{fieldHasContent(title) ? <PrismicRichText field={title} /> : defaultTitle}</Title>
          <Description>
            {fieldHasContent(countryDescription) && <PrismicRichText field={countryDescription} />}
            {fieldHasContent(description) ? (
              <PrismicRichText field={description} />
            ) : (
              <>
                <div>
                  The benefits below are part of Remote’s basic compensation package for all
                  employees in <span>{countryName} </span>. Employees will be able to choose between
                  the PPO and HDHP healthcare offerings.
                </div>
                <br />
                <div>
                  The rates listed here are for the current plan year ({currentYear}). Pricing and
                  benefits are subject to change in response to business and market changes and we
                  will update you when those changes take place.
                </div>
                <br />
                <div>
                  Please email <a href={`mailto:${supportEmail}`}>{supportEmail}</a> with any
                  questions!
                </div>
              </>
            )}
          </Description>
        </div>
        <Table>
          <TitleRow>
            <Column $opacity={firstReducedOpacity}>
              {firstIcon !== 'no icon' && <IconWrapper>{icons[firstIcon] || null}</IconWrapper>}
              <ColumnTitle>
                {firstColumnTitle && <PrismicRichText field={firstColumnTitle} />}
              </ColumnTitle>
            </Column>
            <Column $opacity={secondReducedOpacity}>
              {secondIcon !== 'no icon' && <IconWrapper>{icons[secondIcon] || null}</IconWrapper>}
              <ColumnTitle>
                {secondColumnTitle && <PrismicRichText field={secondColumnTitle} />}
              </ColumnTitle>
            </Column>
            {!column && (
              <Column $opacity={thirdReducedOpacity}>
                {thirdIcon !== 'no icon' && <IconWrapper>{icons[thirdIcon] || null}</IconWrapper>}
                <ColumnTitle>
                  {thirdColumnTitle && <PrismicRichText field={thirdColumnTitle} />}
                </ColumnTitle>
              </Column>
            )}
          </TitleRow>
          <div>
            {table.map(
              (
                {
                  first_column: firstColumn,
                  second_column: secondColumn,
                  third_column: thirdColumn,
                },
                index
              ) => (
                <TableRow key={index}>
                  <ColumnItem $opacity={firstReducedOpacity} $empty={!fieldHasContent(firstColumn)}>
                    <PrismicRichText field={firstColumn} />
                    <IconContainer>
                      <CloseIconWrapper>
                        <CloseIcon />
                      </CloseIconWrapper>
                    </IconContainer>
                  </ColumnItem>
                  <ColumnItem
                    $opacity={secondReducedOpacity}
                    $empty={!fieldHasContent(secondColumn)}
                  >
                    <PrismicRichText field={secondColumn} />
                    <IconContainer>
                      <CloseIconWrapper>
                        <CloseIcon />
                      </CloseIconWrapper>
                    </IconContainer>
                  </ColumnItem>
                  {!column && (
                    <ColumnItem
                      $opacity={thirdReducedOpacity}
                      $empty={!fieldHasContent(thirdColumn)}
                    >
                      <PrismicRichText field={thirdColumn} />
                      <IconContainer>
                        <CloseIconWrapper>
                          <CloseIcon />
                        </CloseIconWrapper>
                      </IconContainer>
                    </ColumnItem>
                  )}
                </TableRow>
              )
            )}
          </div>
        </Table>

        <MobileTable>
          <div>
            {table.map(
              (
                {
                  first_column: firstColumn,
                  second_column: secondColumn,
                  third_column: thirdColumn,
                },
                index
              ) => (
                <MobileTableRow key={index}>
                  <ColumnItem $opacity={thirdReducedOpacity} $empty={!fieldHasContent(firstColumn)}>
                    <Column>
                      <MobileColumnTitle>
                        {firstIcon !== 'no icon' && (
                          <IconWrapper>{icons[firstIcon] || null}</IconWrapper>
                        )}
                        <ColumnTitle>
                          {firstColumnTitle && <PrismicRichText field={firstColumnTitle} />}
                        </ColumnTitle>
                      </MobileColumnTitle>
                    </Column>
                    <PrismicRichText field={firstColumn} />
                    <IconContainer>
                      <CloseIconWrapper>
                        <CloseIcon />
                      </CloseIconWrapper>
                    </IconContainer>
                  </ColumnItem>
                  <ColumnItem
                    $opacity={thirdReducedOpacity}
                    $empty={!fieldHasContent(secondColumn)}
                  >
                    <Column>
                      <MobileColumnTitle>
                        {secondIcon !== 'no icon' && (
                          <IconWrapper>{icons[secondIcon] || null}</IconWrapper>
                        )}
                        <ColumnTitle>
                          {secondColumnTitle && <PrismicRichText field={secondColumnTitle} />}
                        </ColumnTitle>
                      </MobileColumnTitle>
                    </Column>
                    <PrismicRichText field={secondColumn} />
                    <IconContainer>
                      <CloseIconWrapper>
                        <CloseIcon />
                      </CloseIconWrapper>
                    </IconContainer>
                  </ColumnItem>
                  {!column && (
                    <ColumnItem
                      $opacity={thirdReducedOpacity}
                      $empty={!fieldHasContent(thirdColumn)}
                    >
                      <Column>
                        <MobileColumnTitle>
                          {thirdIcon !== 'no icon' && (
                            <IconWrapper>{icons[thirdIcon] || null}</IconWrapper>
                          )}
                          <ColumnTitle>
                            {thirdColumnTitle && <PrismicRichText field={thirdColumnTitle} />}
                          </ColumnTitle>
                        </MobileColumnTitle>
                      </Column>
                      <PrismicRichText field={thirdColumn} />
                      <IconContainer>
                        <CloseIconWrapper>
                          <CloseIcon />
                        </CloseIconWrapper>
                      </IconContainer>
                    </ColumnItem>
                  )}
                </MobileTableRow>
              )
            )}
          </div>
        </MobileTable>
        {fieldHasContent(note) && (
          <Note>
            <PrismicRichText field={note} />
          </Note>
        )}
      </ContentContainer>
    </Section>
  );
}
